import React, { useState, useEffect } from "react";
import {
  FaInstagram,
  FaYoutube,
  FaTiktok,
  FaTwitter,
  FaGlobe,
  FaPhone,
  FaMapMarkerAlt,
  FaUser,
  FaCalendar,
  FaInfoCircle,
  FaEdit,
  FaEnvelope,
  FaBuilding,
  FaArrowLeft,
  FaUsers,
  FaHeart,
  FaPhotoVideo,
  FaLock,
  FaCheckCircle,
  FaExternalLinkAlt
} from "react-icons/fa";
import { ImFacebook2 } from "react-icons/im";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useTheme } from "../../contexts/ThemeContext.tsx";
import { getTheme } from "../../theme.ts";
import { useLoading } from "../../layouts/DashboardLayout.tsx";

interface SocialInfo {
  username?: string;
  full_name?: string;
  biography?: string;
  followers?: number;
  following?: number;
  is_private?: boolean;
  is_verified?: boolean;
  external_url?: string;
  profile_pic_url?: string;
  media_count?: number;
  error?: string;
}

interface UserProfileData {
  id: string;
  user?: {
    id: string;
    email: string;
    role: string;
    profile?: any;
  };
  first_name?: string;
  last_name?: string;
  email?: string;
  role?: string;
  phone_number?: string;
  date_of_birth?: string;
  district?: string;
  state?: string;
  pincode?: string;
  profile_id?: string;
  interests?: string[] | string;
  profile_links?: any;
  profile_picture?: string;
  about?: string;
  bio?: string;
  location?: string;
  website?: string;
  is_verified?: boolean;
  created_at?: string;
}

const UserProfile: React.FC = () => {
  const { themeName } = useTheme();
  const currentTheme = getTheme(themeName);
  const { setIsLoading } = useLoading();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<any>(null);
  const [user, setUser] = useState<any>(null);
  const [error, setError] = useState(false);
  const [socialData, setSocialData] = useState<{[key: string]: SocialInfo}>({});
  const [loadingSocial, setLoadingSocial] = useState<{[key: string]: boolean}>({});
  const { id } = useParams();
  
  const rawData = localStorage.getItem("user");
  const cred = rawData ? JSON.parse(rawData) : null;
  const token = cred?.access;
  const profileId = id;

  console.log('🔍 UserProfile component mounted');
  console.log('📌 Profile ID from params:', profileId);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!profileId) {
        console.log('❌ No profile ID provided');
        setError(true);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(false);
      setIsLoading(true);
      
      try {
        console.log('🔄 Fetching profile for ID:', profileId);
        
        const response = await axios.get(
          `${process.env.REACT_APP_DB_URI}/users/profile/profile-by-user/${profileId}/`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        console.log('📨 Full API Response:', response);
        console.log('📊 Response data:', response?.data);
        
        if (response?.data) {
          console.log('✅ Profile data received');
          // Handle different response structures
          if (response.data.user) {
            setProfile(response.data.user.profile);
            setUser(response.data.user);
          } else if (response.data.profile) {
            setProfile(response.data.profile);
            setUser(response.data);
          } else {
            // If the response is the profile itself
            setProfile(response.data);
            setUser({ email: response.data.email, role: response.data.role });
          }
        } else {
          console.log('❌ No data in response');
          setError(true);
        }
      } catch (err: any) {
        console.error('💥 Error fetching profile:', err);
        console.log('Error details:', err.response?.data);
        setError(true);
      } finally {
        console.log('🏁 Fetch completed');
        setLoading(false);
        setIsLoading(false);
      }
    };

    if (profileId) {
      fetchProfile();
    }
  }, [profileId, token]);

  // Fetch social info when profile links are available
  useEffect(() => {
    if (profile?.profile_links) {
      fetchSocialInfoForAllLinks();
    }
  }, [profile?.profile_links]);

  const fetchSocialInfoForAllLinks = async () => {
    const links = getProfileLinks(profile.profile_links);
    
    for (const link of links) {
      await fetchSocialInfo(link.platform, link.url);
    }
  };

  const fetchSocialInfo = async (platform: string, profileUrl: string) => {
    // Only fetch for supported platforms
    const supportedPlatforms = ['instagram', 'facebook', 'youtube', 'tiktok', 'twitter'];
    if (!supportedPlatforms.includes(platform.toLowerCase())) {
      return;
    }

    setLoadingSocial(prev => ({ ...prev, [platform]: true }));

    try {
      console.log(`🔄 Fetching social info for ${platform}:`, profileUrl);
      
      const response = await axios.get(
        `${process.env.REACT_APP_DB_URI}/users/social-info/fetch/`,
        {
          params: {
            profile_url: profileUrl
          },
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          }
        }
      );

      console.log(`✅ Social info for ${platform}:`, response.data);
      setSocialData(prev => ({
        ...prev,
        [platform]: response.data
      }));

    } catch (err: any) {
      console.error(`💥 Error fetching social info for ${platform}:`, err);
      setSocialData(prev => ({
        ...prev,
        [platform]: { error: 'Failed to fetch social data' }
      }));
    } finally {
      setLoadingSocial(prev => ({ ...prev, [platform]: false }));
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const renderPlatformIcon = (platform: string) => {
    const iconClass = "w-5 h-5";
    switch (platform.toLowerCase()) {
      case "instagram":
        return <FaInstagram className={`${iconClass} text-pink-600`} />;
      case "youtube":
        return <FaYoutube className={`${iconClass} text-red-600`} />;
      case "tiktok":
        return <FaTiktok className={`${iconClass} ${themeName === 'dark' ? 'text-white' : 'text-black'}`} />;
      case "twitter":
      case "x":
        return <FaTwitter className={`${iconClass} text-blue-400`} />;
      case "facebook":
        return <ImFacebook2 className={`${iconClass} text-blue-700`} />;
      default:
        return <FaGlobe className={`${iconClass} ${themeName === 'dark' ? 'text-gray-300' : 'text-gray-600'}`} />;
    }
  };

  const getProfileLinks = (linksObj: any) => {
    if (!linksObj || typeof linksObj !== "object") return [];
    return Object.entries(linksObj)
      .map(([platform, data]: [string, any]) => {
        if (data?.link) {
          return { platform, url: data.link };
        }
        return null;
      })
      .filter(Boolean) as { platform: string; url: string }[];
  };

  // Format numbers with commas
  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat().format(num);
  };

  // Check if social data has valid information (not just errors or empty data)
  const hasValidSocialData = (data: SocialInfo): boolean => {
    if (!data || data.error) return false;
    
    // Check if we have at least one meaningful piece of data
    return !!(data.followers || data.following || data.media_count || data.full_name || data.username || data.biography);
  };

  // Check if we have any valid social data to display
  const hasAnyValidSocialData = (): boolean => {
    return Object.values(socialData).some(data => hasValidSocialData(data));
  };

  // Render social media stats card
  const renderSocialStats = (platform: string, data: SocialInfo) => {
    if (!hasValidSocialData(data)) {
      return null; // Don't render anything if no valid data
    }

    return (
      <div className="space-y-3">
        {/* Profile Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="font-semibold truncate" style={{ color: currentTheme.text }}>
                {data.full_name || data.username}
              </h4>
              {data.is_verified && (
                <FaCheckCircle className="text-blue-500 flex-shrink-0 w-4 h-4" />
              )}
              {data.is_private && (
                <FaLock className="text-gray-500 flex-shrink-0 w-3 h-3" />
              )}
            </div>
            <p style={{ color: currentTheme.textSecondary }} className="text-sm truncate">
              @{data.username}
            </p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="p-2 rounded-lg" style={{ backgroundColor: themeName === 'dark' ? 'rgba(255,255,255,0.05)' : `${currentTheme.primary}10` }}>
            <FaUsers className="mx-auto mb-1" style={{ color: currentTheme.primary }} />
            <div style={{ color: currentTheme.text }} className="font-bold text-sm">
              {data.followers ? formatNumber(data.followers) : '0'}
            </div>
            <div style={{ color: currentTheme.textSecondary }} className="text-xs">
              Followers
            </div>
          </div>
          
          <div className="p-2 rounded-lg" style={{ backgroundColor: themeName === 'dark' ? 'rgba(255,255,255,0.05)' : `${currentTheme.primary}10` }}>
            <FaHeart className="mx-auto mb-1" style={{ color: currentTheme.primary }} />
            <div style={{ color: currentTheme.text }} className="font-bold text-sm">
              {data.following ? formatNumber(data.following) : '0'}
            </div>
            <div style={{ color: currentTheme.textSecondary }} className="text-xs">
              Following
            </div>
          </div>
          
          <div className="p-2 rounded-lg" style={{ backgroundColor: themeName === 'dark' ? 'rgba(255,255,255,0.05)' : `${currentTheme.primary}10` }}>
            <FaPhotoVideo className="mx-auto mb-1" style={{ color: currentTheme.primary }} />
            <div style={{ color: currentTheme.text }} className="font-bold text-sm">
              {data.media_count ? formatNumber(data.media_count) : '0'}
            </div>
            <div style={{ color: currentTheme.textSecondary }} className="text-xs">
              Posts
            </div>
          </div>
        </div>

        {/* Bio */}
        {data.biography && (
          <div className="mt-3">
            <p style={{ color: currentTheme.textSecondary }} className="text-sm line-clamp-3">
              {data.biography}
            </p>
          </div>
        )}
      </div>
    );
  };

  // Format interests for display
  const formatInterests = (interests: any): string => {
    if (!interests) return "No interests specified";

    if (Array.isArray(interests)) {
      return interests.filter(item => item && item.trim()).join(", ");
    }
    
    if (typeof interests === 'string') {
      try {
        const parsed = JSON.parse(interests);
        if (Array.isArray(parsed)) {
          return parsed.filter(item => item && item.trim()).join(", ");
        }
        return interests;
      } catch {
        return interests;
      }
    }
    
    return "No interests specified";
  };

  // Get display name
  const getDisplayName = () => {
    if (profile?.first_name && profile?.last_name) {
      return `${profile.first_name} ${profile.last_name}`;
    }
    if (profile?.first_name) {
      return profile.first_name;
    }
    return "User";
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: `linear-gradient(135deg, ${currentTheme.background} 0%, ${currentTheme.surface} 100%)`,
          padding: "1.5rem",
        }}
        className="flex items-center justify-center"
      >
        <div className="text-center">
          <div 
            className="loader border-t-4 rounded-full w-12 h-12 animate-spin mx-auto mb-4"
            style={{ borderColor: currentTheme.primary }}
          ></div>
          <p style={{ color: currentTheme.textSecondary }}>Loading user profile...</p>
          <p style={{ color: currentTheme.textSecondary }} className="text-sm mt-2">User ID: {profileId}</p>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: `linear-gradient(135deg, ${currentTheme.background} 0%, ${currentTheme.surface} 100%)`,
          padding: "1.5rem",
        }}
        className="flex items-center justify-center"
      >
        <div
          className="p-8 rounded-3xl text-center max-w-md w-full backdrop-blur-sm"
          style={{
            backgroundColor: themeName === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.8)',
            border: `1px solid ${themeName === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          }}
        >
          <div 
            className="text-6xl mb-4 mx-auto w-24 h-24 rounded-full flex items-center justify-center"
            style={{ backgroundColor: `${currentTheme.primary}20` }}
          >
            👤
          </div>
          <h2
            className="text-2xl font-bold mb-2"
            style={{ color: currentTheme.primary }}
          >
            {error ? "Error Loading Profile" : "Profile Not Found"}
          </h2>
          <p style={{ color: currentTheme.textSecondary }} className="mb-4">
            {error ? "Failed to load user profile. Please try again." : "The user profile you are looking for does not exist."}
          </p>
          <p style={{ color: currentTheme.textSecondary }} className="text-sm mb-4">
            User ID: {profileId}
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={handleBack}
              style={{
                backgroundColor: currentTheme.primary,
                color: currentTheme.surface,
              }}
              className="px-6 py-3 rounded-xl hover:opacity-90 transition-all duration-300 font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              Go Back
            </button>
            {error && (
              <button
                onClick={() => window.location.reload()}
                style={{
                  backgroundColor: themeName === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                  color: currentTheme.text,
                }}
                className="px-6 py-3 rounded-xl hover:opacity-90 transition-all duration-300 font-medium border hover:shadow-lg"
              >
                Try Again
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${currentTheme.background} 0%, ${currentTheme.surface} 100%)`,
        padding: "1.5rem",
      }}
      className="md:p-6"
    >
      {/* Back Button */}
      <div className="mb-6">
        <button
          onClick={handleBack}
          style={{
            color: currentTheme.primary,
            backgroundColor: themeName === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.8)',
            border: `1px solid ${themeName === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
          }}
          className="flex items-center gap-3 px-5 py-3 rounded-2xl hover:opacity-90 transition-all duration-300 backdrop-blur-sm shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
        >
          <FaArrowLeft size={18} />
          <span className="font-semibold">Back</span>
        </button>
      </div>

      <div className="space-y-8 max-w-7xl mx-auto">
        {/* Profile Header */}
        <div
          className="rounded-3xl p-8 flex flex-col md:flex-row items-center gap-8 transition-all duration-300 backdrop-blur-sm"
          style={{
            backgroundColor: themeName === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.8)',
            border: `1px solid ${themeName === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          }}
        >
          <div className="relative">
            <div
              className="w-32 h-32 md:w-40 md:h-40 rounded-full overflow-hidden shadow-2xl"
              style={{
                border: `4px solid ${currentTheme.primary}30`,
                background: `linear-gradient(135deg, ${currentTheme.primary}20, ${currentTheme.primary}10)`,
              }}
            >
              <img
                src={
                  profile?.profile_picture
                    ? `${process.env.REACT_APP_PF}${profile.profile_picture}`
                    : "/default-avatar.jpg"
                }
                alt={getDisplayName()}
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = "/default-avatar.jpg";
                }}
              />
            </div>
          </div>

          <div className="text-center md:text-left flex-1">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
              <div>
                <h1
                  className="text-3xl md:text-4xl font-bold mb-2 bg-gradient-to-r from-current to-current bg-clip-text text-transparent"
                  style={{ 
                    backgroundImage: `linear-gradient(135deg, ${currentTheme.primary}, ${currentTheme.secondary || currentTheme.primary})`
                  }}
                >
                  {getDisplayName()}
                </h1>
                <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                  <div className="flex items-center gap-2">
                    <FaEnvelope style={{ color: currentTheme.primary }} className="w-4 h-4" />
                    <span style={{ color: currentTheme.textSecondary }} className="text-sm">
                      {user?.email || profile?.email || "Email not provided"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <FaMapMarkerAlt style={{ color: currentTheme.primary }} className="w-4 h-4" />
                    <span style={{ color: currentTheme.textSecondary }} className="text-sm">
                      {profile.district || "Unknown District"}, {profile.state || "Unknown State"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Bio Section */}
            {(profile.about || profile.bio) && (
              <div className="mt-4 p-4 rounded-2xl" style={{ backgroundColor: themeName === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)' }}>
                <p style={{ color: currentTheme.textSecondary }} className="text-sm leading-relaxed">
                  {profile.about || profile.bio}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Grid Sections */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Personal Info */}
          <div
            className="rounded-3xl p-6 transition-all duration-300 backdrop-blur-sm hover:shadow-2xl transform hover:-translate-y-1"
            style={{
              backgroundColor: themeName === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.8)',
              border: `1px solid ${themeName === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
              boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
            }}
          >
            <h3
              className="text-xl font-semibold mb-4 flex items-center gap-3 pb-3"
              style={{
                color: currentTheme.primary,
                borderBottom: `2px solid ${currentTheme.primary}20`,
              }}
            >
              <div className="p-2 rounded-xl" style={{ backgroundColor: `${currentTheme.primary}15` }}>
                <FaUser style={{ color: currentTheme.primary }} />
              </div>
              Personal Info
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center py-2 border-b" style={{ borderColor: themeName === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}>
                <span style={{ color: currentTheme.textSecondary }} className="font-medium">First Name</span>
                <span style={{ color: currentTheme.text }} className="font-semibold">
                  {profile.first_name || "Not provided"}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b" style={{ borderColor: themeName === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}>
                <span style={{ color: currentTheme.textSecondary }} className="font-medium">Last Name</span>
                <span style={{ color: currentTheme.text }} className="font-semibold">
                  {profile.last_name || "Not provided"}
                </span>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div
            className="rounded-3xl p-6 transition-all duration-300 backdrop-blur-sm hover:shadow-2xl transform hover:-translate-y-1"
            style={{
              backgroundColor: themeName === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.8)',
              border: `1px solid ${themeName === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
              boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
            }}
          >
            <h3
              className="text-xl font-semibold mb-4 flex items-center gap-3 pb-3"
              style={{
                color: currentTheme.primary,
                borderBottom: `2px solid ${currentTheme.primary}20`,
              }}
            >
              <div className="p-2 rounded-xl" style={{ backgroundColor: `${currentTheme.primary}15` }}>
                <FaPhone style={{ color: currentTheme.primary , transform: 'rotate(180deg)' }} />
              </div>
              Contact Information
            </h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center py-2 border-b" style={{ borderColor: themeName === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}>
                <span style={{ color: currentTheme.textSecondary }} className="font-medium">Email</span>
                <span style={{ color: currentTheme.text }} className="font-semibold text-sm truncate ml-2">
                  {user?.email || profile?.email || "Not provided"}
                </span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span style={{ color: currentTheme.textSecondary }} className="font-medium">Address</span>
                <span style={{ color: currentTheme.text }} className="font-semibold text-right text-sm">
                  {profile.district && profile.state
                    ? `${profile.district}, ${profile.state}${profile.pincode ? ` - ${profile.pincode}` : ""}`
                    : "Not provided"}
                </span>
              </div>
            </div>
          </div>

          {/* Social Links */}
          <div
            className="rounded-3xl p-6 transition-all duration-300 backdrop-blur-sm hover:shadow-2xl transform hover:-translate-y-1"
            style={{
              backgroundColor: themeName === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.8)',
              border: `1px solid ${themeName === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
              boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
            }}
          >
            <h3
              className="text-xl font-semibold mb-4 flex items-center gap-3 pb-3"
              style={{
                color: currentTheme.primary,
                borderBottom: `2px solid ${currentTheme.primary}20`,
              }}
            >
              <div className="p-2 rounded-xl" style={{ backgroundColor: `${currentTheme.primary}15` }}>
                <FaGlobe style={{ color: currentTheme.primary }} />
              </div>
              Social Links
            </h3>
            <div className="flex flex-wrap gap-3 mt-4">
              {getProfileLinks(profile.profile_links).length > 0 ? (
                getProfileLinks(profile.profile_links).map((link, idx) => (
                  <a
                    key={idx}
                    href={
                      link.url.startsWith("http")
                        ? link.url
                        : `https://${link.url}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-4 rounded-2xl hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl group"
                    style={{
                      backgroundColor: themeName === 'dark' ? 'rgba(255,255,255,0.1)' : `${currentTheme.primary}10`,
                      border: `1px solid ${themeName === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                    }}
                  >
                    <div className="flex items-center gap-2">
                      {renderPlatformIcon(link.platform)}
                      <span 
                        className="text-sm font-medium group-hover:underline"
                        style={{ color: currentTheme.text }}
                      >
                        {link.platform}
                      </span>
                    </div>
                  </a>
                ))
              ) : (
                <div className="text-center w-full py-4">
                  <p
                    className="text-sm"
                    style={{ color: currentTheme.textSecondary }}
                  >
                    No social links
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Social Media Analytics - Only show if we have valid data */}
        {hasAnyValidSocialData() && (
          <div
            className="rounded-3xl p-6 transition-all duration-300 backdrop-blur-sm hover:shadow-2xl"
            style={{
              backgroundColor: themeName === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.8)',
              border: `1px solid ${themeName === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
              boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
            }}
          >
            <h3
              className="text-xl font-semibold mb-4 flex items-center gap-3 pb-3"
              style={{
                color: currentTheme.primary,
                borderBottom: `2px solid ${currentTheme.primary}20`,
              }}
            >
              <div className="p-2 rounded-xl" style={{ backgroundColor: `${currentTheme.primary}15` }}>
                <FaUsers style={{ color: currentTheme.primary }} />
              </div>
              Social Media Analytics
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(socialData)
                .filter(([platform, data]) => hasValidSocialData(data))
                .map(([platform, data]) => (
                  <div
                    key={platform}
                    className="rounded-2xl p-4 transition-all duration-300 hover:shadow-lg transform hover:-translate-y-0.5"
                    style={{
                      backgroundColor: themeName === 'dark' ? 'rgba(255,255,255,0.05)' : `${currentTheme.primary}05`,
                      border: `1px solid ${themeName === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                    }}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 rounded-xl" style={{ backgroundColor: themeName === 'dark' ? 'rgba(255,255,255,0.1)' : `${currentTheme.primary}10` }}>
                        {renderPlatformIcon(platform)}
                      </div>
                      <h4
                        className="font-semibold capitalize flex-1"
                        style={{ color: currentTheme.text }}
                      >
                        {platform}
                      </h4>
                      {loadingSocial[platform] && (
                        <div 
                          className="w-4 h-4 border-2 border-transparent rounded-full animate-spin" 
                          style={{ 
                            borderTopColor: currentTheme.primary,
                            borderRightColor: currentTheme.primary 
                          }} 
                        />
                      )}
                    </div>
                    {renderSocialStats(platform, data)}
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfile;